import React from 'react';
import { Dimensions, PixelRatio, Platform } from 'react-native';

// Retrieve initial screen's width
let screenWidth = Dimensions.get('window').width;

// Retrieve initial screen's height
let screenHeight = Dimensions.get('window').height;
export const isMobile = screenWidth <= 768;

/**
 * Converts provided width percentage to independent pixel (dp).
 * @param {string | number} widthPercent - The percentage of screen's width (e.g., 50 or "50")
 * @return {number} The calculated dp depending on current device's screen width.
 */
export const widthPercentageToDP = (widthPercent: string | number) => {
  // Parse string percentage input and convert it to number.
  const elemWidth =
    typeof widthPercent === 'number'
      ? widthPercent
      : parseFloat(String(widthPercent));
  const { width, height } = Dimensions.get('window');
  const isMobile = width <= 768;

  // Use the expected value from Typography.ts (width: 72) to reverse-engineer the base dimension
  // For mobile, wp(9) should be 72, so base width * 0.09 = 72 => base width = 800
  // For tablet, wp(5.25) should be 72, so base width * 0.0525 = 72 => base width = 1371
  const mobileBaseWidth = 800;
  const tabletBaseWidth = 1371;

  const baseWidth = isMobile ? mobileBaseWidth : tabletBaseWidth;

  return PixelRatio.roundToNearestPixel((baseWidth * elemWidth) / 100);
};

/**
 * Converts provided height percentage to independent pixel (dp).
 * @param {string | number} heightPercent - The percentage of screen's height (e.g., 50 or "50")
 * @return {number} The calculated dp depending on current device's screen height.
 */
export const heightPercentageToDP = (heightPercent: string | number) => {
  // Parse string percentage input and convert it to number.
  const elemHeight =
    typeof heightPercent === 'number'
      ? heightPercent
      : parseFloat(String(heightPercent));
  const { width, height } = Dimensions.get('window');
  const isMobile = width <= 768;

  // Use the expected value from Typography.ts (height: 72) to reverse-engineer the base dimension
  // For mobile, hp(9) should be 72, so base height * 0.09 = 72 => base height = 800
  // For tablet, hp(7) should be 72, so base height * 0.07 = 72 => base height = 1028
  const mobileBaseHeight = 800;
  const tabletBaseHeight = 1028;

  const baseHeight = isMobile ? mobileBaseHeight : tabletBaseHeight;

  return PixelRatio.roundToNearestPixel((baseHeight * elemHeight) / 100);
};

// Shorter aliases for convenience
export const wp = widthPercentageToDP;
export const hp = heightPercentageToDP;

/**
 * Get current screen dimensions
 * @return {object} Object containing width and height
 */
export const getScreenDimensions = () => ({
  width: screenWidth,
  height: screenHeight,
});

/**
 * Check if device is in landscape mode
 * @return {boolean} True if landscape, false if portrait
 */
export const isLandscape = () => screenWidth > screenHeight;

/**
 * Check if device is in portrait mode
 * @return {boolean} True if portrait, false if landscape
 */
export const isPortrait = () => screenWidth <= screenHeight;

/**
 * Get current orientation
 * @return {string} 'portrait' or 'landscape'
 */
export const getOrientation = () =>
  screenWidth < screenHeight ? 'portrait' : 'landscape';

/**
 * Event listener function that detects orientation change and triggers screen rerendering.
 * For CLASS COMPONENTS: Updates state with orientation change.
 * @deprecated Use useResponsiveDimensions hook for functional components instead
 * @param {object} component - Screen's class component (this variable)
 */
export const listenOrientationChange = (component: React.Component) => {
  const subscription = Dimensions.addEventListener(
    'change',
    ({ window: newDimensions }) => {
      // Retrieve and save new dimensions
      screenWidth = newDimensions.width;
      screenHeight = newDimensions.height;

      // Trigger screen's rerender with a state update of the orientation variable
      if (component && component.setState) {
        component.setState({
          orientation: screenWidth < screenHeight ? 'portrait' : 'landscape',
        });
      }
    },
  );

  // Return subscription for manual cleanup if needed
  return subscription;
};

/**
 * Removes orientation change listener
 * Should be invoked in componentWillUnmount lifecycle method
 * @deprecated The old API is deprecated, use the subscription.remove() instead
 */
export const removeOrientationListener = () => {
  // Note: The new API requires you to call remove() on the subscription returned by addEventListener
  // This function is kept for backwards compatibility but should not be relied upon
  // Dimensions.removeEventListener('change', () => {}); // This is deprecated and doesn't work with the new API
};

/**
 * React Hook for responsive dimensions (For Functional Components)
 * Automatically updates dimensions on orientation change
 * @return {object} Object containing width, height, and orientation
 *
 * Usage:
 * const { width, height, orientation } = useResponsiveDimensions();
 */
export const useResponsiveDimensions = () => {
  const [dimensions, setDimensions] = React.useState({
    width: screenWidth,
    height: screenHeight,
    orientation: getOrientation(),
  });

  React.useEffect(() => {
    const subscription = Dimensions.addEventListener(
      'change',
      ({ window: newDimensions }) => {
        screenWidth = newDimensions.width;
        screenHeight = newDimensions.height;

        setDimensions({
          width: screenWidth,
          height: screenHeight,
          orientation: screenWidth < screenHeight ? 'portrait' : 'landscape',
        });
      },
    );

    // Cleanup subscription on unmount
    return () => subscription?.remove();
  }, []);

  return dimensions;
};

/**
 * Returns platform-specific value
 * For web: returns the fixed pixel value
 * For native (iOS/Android): returns the responsive wp/hp value
 *
 * @param {number} webValue - Fixed pixel value for web
 * @param {number} responsiveValue - wp() or hp() value for native platforms
 * @return {number} Platform-specific value
 *
 * Usage:
 * width: platformValue(20, wp(5))
 * padding: platformValue(12, hp(1.5))
 */
export const platformValue = (
  webValue: number,
  responsiveValue: number,
): number => {
  return Platform.OS === 'web' ? webValue : responsiveValue;
};
